<template>
  <DashboardPanel title="物业费收缴趋势分析">
    <div class="fee-trend">
      <div class="fee-summary">
        <article v-for="item in summary" :key="item.label"><span>{{ item.label }}</span><strong :class="item.className"><i :class="item.icon"></i>{{ item.value }}<small>{{ item.unit }}</small></strong></article>
      </div>
      <div class="fee-chart-head"><span>金额（万元）</span><div><i class="blue"></i>应收金额（万元）<i class="green"></i>实收金额（万元）<i class="yellow"></i>缴费率（%）</div><span>缴费率（%）</span></div>
      <div ref="chart" class="fee-chart"></div>
      <p class="fee-note">注：金额与趋势均来自大屏聚合接口返回快照</p>
    </div>
  </DashboardPanel>
</template>

<script>
import * as echarts from 'echarts'
import DashboardPanel from './DashboardPanel.vue'
import { axisScale, dashboardTooltip, formatNumber, percentValue, roundNumber, toNumber } from '../dashboardFormat'
export default {
  name: 'FeeTrend', components: { DashboardPanel },
  props: { data: { type: Object, default: () => ({}) } },
  data: () => ({ chart: null }),
  computed: {
    summary() {
      const source = this.data.summary || {}
      const receivable = toNumber(source.receivableAmount)
      const received = toNumber(source.receivedAmount)
      const owing = source.oweAmount === undefined || source.oweAmount === null
        ? Math.max(roundNumber(receivable - received), 0)
        : toNumber(source.oweAmount)
      return [
        { label: '应收金额', value: this.money(receivable), unit: '万元', icon: 'el-icon-document', className: '' },
        { label: '实收金额', value: this.money(received), unit: '万元', icon: 'el-icon-money', className: '' },
        { label: '欠费金额', value: this.money(owing), unit: '万元', icon: 'el-icon-data-analysis', className: 'danger' },
        { label: '综合缴费率', value: this.rate(receivable, received), unit: '', icon: 'el-icon-pie-chart', className: 'success' }
      ]
    },
    trend() { return Array.isArray(this.data.trend) ? this.data.trend : [] }
  },
  watch: { data: { deep: true, handler() { this.renderChart() } } },
  mounted() { this.renderChart(); window.addEventListener('resize', this.resizeChart) },
  beforeDestroy() { window.removeEventListener('resize', this.resizeChart); if (this.chart) this.chart.dispose() },
  methods: {
    renderChart() {
      if (!this.chart) this.chart = echarts.init(this.$refs.chart)
      const receivable = this.trend.map(item => roundNumber(toNumber(item.receivableAmount) / 10000, 2))
      const received = this.trend.map(item => roundNumber(toNumber(item.receivedAmount) / 10000, 2))
      const rates = this.trend.map(item => this.rate(toNumber(item.receivableAmount), toNumber(item.receivedAmount), false))
      const amountMax = Math.max.apply(null, receivable.concat(received).concat([1]))
      const amountAxis = axisScale(amountMax)
      this.chart.setOption({
        grid: { top: 23, right: 42, bottom: 28, left: 38 },
        tooltip: dashboardTooltip({
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const rows = Array.isArray(params) ? params : [params]
            const title = rows[0] ? rows[0].axisValue : ''
            return [title].concat(rows.map(item => {
              const unit = item.seriesName === '缴费率' ? '%' : ' 万元'
              return `${item.marker}${item.seriesName}：${formatNumber(item.value, item.seriesName === '缴费率' ? 1 : 2)}${unit}`
            })).join('<br/>')
          }
        }),
        xAxis: { type: 'category', data: this.trend.map(item => this.formatBucket(item.bucket)), axisLine: { lineStyle: { color: '#395b70' } }, axisTick: { show: false }, axisLabel: { color: '#9eb3c2', fontSize: 10 } },
        yAxis: [
          { type: 'value', min: 0, max: amountAxis.max, interval: amountAxis.interval, splitLine: { lineStyle: { color: 'rgba(65,122,159,.18)', type: 'dashed' } }, axisLabel: { color: '#a5b5c1', fontSize: 10, formatter: value => formatNumber(value, amountAxis.interval < 1 ? 1 : 0) } },
          { type: 'value', min: 0, max: 100, interval: 20, splitLine: { show: false }, axisLabel: { formatter: '{value}%', color: '#a5b5c1', fontSize: 10 } }
        ],
        series: [
          { name: '应收金额', type: 'bar', barWidth: 19, data: receivable, itemStyle: { color: '#3289ed' }, label: { show: true, position: 'top', color: '#dce8f1', fontSize: 9 } },
          { name: '实收金额', type: 'bar', barWidth: 19, data: received, itemStyle: { color: '#3cb66a' }, label: { show: true, position: 'top', color: '#dce8f1', fontSize: 9 } },
          { name: '缴费率', type: 'line', yAxisIndex: 1, smooth: false, data: rates, symbol: 'circle', symbolSize: 7, lineStyle: { color: '#ffc32a', width: 2 }, itemStyle: { color: '#ffc32a', borderColor: '#fff1b0', borderWidth: 1 }, label: { show: true, position: 'top', formatter: '{c}%', color: '#f0f3f5', fontSize: 9 } }
        ]
      })
    },
    resizeChart() { if (this.chart) this.chart.resize() },
    formatBucket(bucket) {
      const value = String(bucket || '')
      return /^\d{4}-\d{2}$/.test(value) ? value.slice(2) : value.slice(5)
    },
    money(value) { return `¥ ${formatNumber(toNumber(value) / 10000, 2)}` },
    rate(receivable, received, withUnit = true) {
      const value = percentValue(received, receivable)
      return withUnit ? `${formatNumber(value, 1)}%` : value
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-trend { height: 100%; padding: 0 12px; }
.fee-summary { display: grid; grid-template-columns: repeat(4, 1fr); height: 80px; margin-top: 8px; background: rgba(8, 49, 78, .65); border: 1px solid rgba(43, 109, 153, .27); border-radius: 0 0 12px 12px; }
.fee-summary article { display: flex; align-items: center; flex-direction: column; justify-content: center; border-right: 1px solid rgba(49, 105, 142, .18); }
.fee-summary article:last-child { border-right: 0; }
.fee-summary span { color: #9eb2c2; font-size: 13px; }
.fee-summary strong { display: flex; align-items: center; margin-top: 10px; color: #f1f7fc; font-size: 20px; font-weight: 600; white-space: nowrap; }
.fee-summary strong > i { width: 32px; height: 32px; margin-right: 7px; padding-top: 7px; border-radius: 50%; background: rgba(13, 123, 190, .22); color: #2d9ae9; font-size: 18px; text-align: center; }
.fee-summary strong.danger { color: #f0524e; }
.fee-summary strong.danger i { background: rgba(208, 67, 66, .2); color: #f0524e; }
.fee-summary strong.success { color: #36c969; }
.fee-summary strong.success i { background: rgba(46, 184, 93, .2); color: #36c969; }
.fee-summary small { margin-left: 4px; color: #aebcc7; font-size: 10px; font-weight: 400; }
.fee-chart-head { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; height: 42px; color: #afc0cd; font-size: 11px; }
.fee-chart-head > span:last-child { text-align: right; }
.fee-chart-head div { display: flex; align-items: center; gap: 8px; }
.fee-chart-head i { display: inline-block; width: 18px; height: 9px; margin-left: 22px; }
.fee-chart-head i:first-child { margin-left: 0; }
.fee-chart-head .blue { background: #338cf2; }.fee-chart-head .green { background: #39ad65; }.fee-chart-head .yellow { position: relative; height: 2px; background: #ffc12b; }.fee-chart-head .yellow::after { position: absolute; top: -3px; left: 7px; width: 7px; height: 7px; border-radius: 50%; background: #ffc12b; content: ''; }
.fee-chart { height: calc(100% - 154px); min-height: 200px; }
.fee-note { height: 18px; margin: 0; color: #526f83; font-size: 9px; }
</style>
